import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Link as MuiLink,
  Divider,
  TextField,
  Button,
  Select,
  MenuItem
} from '@mui/material';
import { PatternFormat } from 'react-number-format';
import CircularProgress from '@mui/material/CircularProgress';

import Header from './Header';
import { getDashboardData, getAuthToken, getUserByPhoneNumber, checkUserValidity, getLateFee } from './service';
import { countryConfig } from './utility';
import { getSkuById } from './constants';

function computeDaysUntilDueFromDate(dueDateObj) {
  const now = new Date();
  const normalizedNow = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const normalizedDue = new Date(
    dueDateObj.getFullYear(),
    dueDateObj.getMonth(),
    dueDateObj.getDate()
  );
  const diffDays = Math.round((normalizedDue - normalizedNow) / (1000 * 60 * 60 * 24));
  return diffDays < 0 ? 0 : diffDays;
}

function getDueText(daysUntilDue) {
  if (daysUntilDue === null) return 'No due date';
  if (daysUntilDue === 0) return 'Due Today';
  if (daysUntilDue === 1) return 'Due Tomorrow';
  return `Due in ${daysUntilDue} Days`;
}

const PhoneNumberFormat = React.forwardRef(function PhoneNumberFormat(props, ref) {
  const { onChange, mask, ...other } = props;
  return (
    <PatternFormat
      {...other}
      format={mask}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            value: values.value
          }
        });
      }}
    />
  );
});

function normalizeDate(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [lateFee, setLateFee] = useState(6);
  
  const { userId } = useParams();
  const navigate = useNavigate();

  const [effectiveUserId, setEffectiveUserId] = useState(userId);
  const [loadingGetUserByPhone, setLoadingGetUserByPhone] = useState(false);
  const [userNotFoundByPhone, setUserNotFoundByPhone] = useState(false);

  useEffect(() => {
    setEffectiveUserId(userId);
  }, [userId]);

  function getSkuImage(skuId) {
    const skuInfo = getSkuById(skuId);
    return skuInfo ? skuInfo.img : null;
  }

  const handlePhoneSubmit = async () => {
    try {
      const token = await getAuthToken();
      const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');
      const fullPhoneNumber = `${countryConfig[selectedCountry].code}${cleanPhoneNumber}`;
      setLoadingGetUserByPhone(true);
      const user = await getUserByPhoneNumber(fullPhoneNumber, token);
      if (user && user.dbUser) {
        setEffectiveUserId(user.dbUser.id);
        setUserNotFoundByPhone(false);
      } else {
        setUserNotFoundByPhone(true);
      }
      setLoadingGetUserByPhone(false);
    } catch (err) {
      setLoadingGetUserByPhone(false);
    }
  };

  useEffect(() => {
    if (!effectiveUserId) {
      setLoading(false);
      return;
    }

    async function fetchDashboardData() {
      setLoading(true);
      try {
        const token = await getAuthToken();

        if (userId && effectiveUserId === userId) {
          const validation = await checkUserValidity(userId, token);
          if (!validation.isValid) {
            setEffectiveUserId(null);
            navigate('/'); 
            return;
          }
        }

        const data = await getDashboardData(effectiveUserId, token);
        setDashboardData(data);
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, [effectiveUserId, userId, navigate]);

  useEffect(() => {
    if(dashboardData && dashboardData.latest_location_id) {
      async function fetchLateFee() {
        const token = await getAuthToken();
        const locationId = dashboardData.latest_location_id;
        const lateFee = await getLateFee(locationId, token);
        setLateFee(lateFee);
      }
      fetchLateFee();
    }
  }, [dashboardData]);

  if (!effectiveUserId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Header />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            mt: 8
          }}
        >
          <Typography variant="h6" align="center" sx={{ maxWidth: 400 }}>
            To access your Re:Dish dashboard, enter the phone number you used when checking out containers.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%', maxWidth: 300 }}>
            <Select
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              size="small"
              sx={{ width: 120, paddingY: 1 }}
            >
              {Object.entries(countryConfig).map(([country, config]) => (
                <MenuItem key={country} value={country}>
                  {country} ({config.code})
                </MenuItem>
              ))}
            </Select>
            <TextField
              fullWidth
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              variant="outlined"
              placeholder={countryConfig[selectedCountry].mask.replace(/#/g, '0')}
              slotProps={{
                input: {
                  inputComponent: PhoneNumberFormat,
                  inputProps: {
                    mask: countryConfig[selectedCountry].mask.replace(/#/g, '#')
                  }
                }
              }}
            />
          </Box>
          {loadingGetUserByPhone ? (
            <CircularProgress />
          ) : (
            <Button
              variant="contained"
              onClick={handlePhoneSubmit}
              sx={{
                backgroundColor: '#36b63f',
                '&:hover': {
                  backgroundColor: '#2b9032'
                }
              }}
            >
              Submit
            </Button>
          )}
          {userNotFoundByPhone && (
            <Typography align="center" variant="body1" sx={{ color: 'red' }}>
              No user found with that phone number. Please check your phone number and try again. Or enter this phone number the next time you check out Re:Dish containers.
            </Typography>
          )}
        </Box>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h5" align="center">
          Loading...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Typography variant="h5" align="center" color="error">
          Error: {error.message}
        </Typography>
      </Container>
    );
  }

  const brandGreen = '#36b63f';

  const activeTransactions = dashboardData.transactions.filter((tx) => {
    const status = (tx.status || '').toLowerCase();
    if (status !== 'pending') return false;
    if (tx.date_charged) return false;
    if (!tx.due_date) return true;
    const dueDate = new Date(tx.due_date);
    const today = new Date();
    return normalizeDate(today) <= normalizeDate(dueDate);
  });

  const groupedTransactions = activeTransactions.reduce((groups, transaction) => {
    const key = transaction.due_date
      ? String(computeDaysUntilDueFromDate(new Date(transaction.due_date)))
      : 'no_due';
    (groups[key] ||= []).push(transaction);
    return groups;
  }, {});

  const numericKeys = Object.keys(groupedTransactions)
    .filter((key) => key !== 'no_due')
    .map(Number)
    .sort((a, b) => a - b)
    .map(String);
  if (groupedTransactions['no_due']) {
    numericKeys.push('no_due');
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4, px: 2 }}>
      <Header userId={effectiveUserId} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.3rem', textAlign: 'left' }}>
          Make an impact with every return!
        </Typography>
        <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 'bold', color: brandGreen, fontSize: '1.3rem', textAlign: 'left' }}>
          Reusables must be returned within 7 days
        </Typography>
        <Typography variant="body2" sx={{ ml: 2, fontSize: '1.2rem', textAlign: 'left' }}>
          • Reusables not returned (or late returns) → ${lateFee.toFixed(2)} charge
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, fontSize: '1.3rem', textAlign: 'left' }}>
        Your Stats:
      </Typography>
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Card sx={{ textAlign: 'center', backgroundColor: brandGreen, color: '#fff' }}>
              <CardContent>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  Reusables Returned
                </Typography>
                <Typography variant="h3" sx={{ fontSize: '2.5rem' }}>
                  {dashboardData.total_returns ?? 'XX'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#eee', color: '#000' }}>
              <CardContent>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  Checked Out
                </Typography>
                <Typography variant="h3" sx={{ fontSize: '2.5rem' }}>
                  {activeTransactions.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 'bold',
            mb: 1,
            fontSize: '1.3rem',
            textAlign: 'left',
            color: 'black'
          }}
        >
          {activeTransactions.length > 0
            ? 'Reusables Currently Checked Out:'
            : 'No reusables currently checked out'}
        </Typography>
        <MuiLink
          component={RouterLink}
          to={`/full-history/${effectiveUserId}`}
          underline="always"
          sx={{
            color: brandGreen,
            fontFamily: 'Morn',
            fontSize: '1.1rem',
            textAlign: 'left',
            display: 'block'
          }}
        >
          See full history
        </MuiLink>
      </Box>
      {activeTransactions.length > 0 &&
        numericKeys.map((groupKey) => {
          const dueHeader = groupKey === 'no_due' ? 'No Due Date' : getDueText(Number(groupKey));
          const transactions = groupedTransactions[groupKey];
          const uniqueSkus = transactions.reduce((acc, tx) => {
            if (!acc[tx.sku_id]) {
              acc[tx.sku_id] = { sku_id: tx.sku_id, count: 0 };
            }
            acc[tx.sku_id].count += 1;
            return acc;
          }, {});
          let uniqueSkuArray = Object.values(uniqueSkus);
          uniqueSkuArray.sort((a, b) => b.count - a.count);
          return (
            <React.Fragment key={groupKey}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: 2,
                  my: 2,
                  width: '100%'
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    gap: 1,
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    paddingBottom: 1,
                    justifyContent: 'flex-end',
                    '&::-webkit-scrollbar': { height: '4px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '2px' }
                  }}
                >
                  {uniqueSkuArray.map((item, idx) => {
                    const skuImg = getSkuImage(item.sku_id);
                    
                    if (!skuImg) return null;
                    return (
                      <Box key={idx} sx={{ position: 'relative', display: 'inline-block', flexShrink: 0, marginRight: '8px' }}>
                        <Box
                          component="img"
                          src={skuImg}
                          alt={`SKU ${item.sku_id}`}
                          sx={{ 
                            height: 60,
                            width: 90,
                            objectFit: 'contain',
                            objectPosition: 'center'
                          }}
                        />
                        {item.count > 1 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              backgroundColor: brandGreen,
                              color: 'white',
                              borderRadius: '50%',
                              width: 20,
                              height: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.75rem',
                              fontFamily: 'Morn'
                            }}
                          >
                            {item.count}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Box
                  sx={{
                    width: '120px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    paddingLeft: '8px'
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', whiteSpace: 'nowrap' }}>
                    {dueHeader}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2, borderColor: '#ccc', borderBottomWidth: 4 }} />
            </React.Fragment>
          );
        })}

      <Typography 
        variant="body2" 
        sx={{ 
          mt: 3, 
          mb: 2, 
          fontStyle: 'italic', 
          color: 'text.secondary',
          textAlign: 'center',
          px: 1
        }}
      >
        * After you return a container, it may take up to two business days for the return to be reflected in your account.
      </Typography>
      
      <Box
        sx={{
          backgroundColor: brandGreen,
          borderRadius: 4,
          p: 3,
          mt: 2,
          mb: 2,
          color: 'white',
          textAlign: 'center',
          pb: 4
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, fontSize: '1.3rem' }}>
          You are making a BIG impact!
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2
          }}
        >
          <Typography variant="h2" component="span" sx={{ fontWeight: '400' }}>
            {dashboardData.total_returns || 0}
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="body1" component="div" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
              {dashboardData.total_returns === 1 ? 'container' : 'containers'}
            </Typography>
            <Typography variant="body1" component="div" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
              diverted from waste
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}